/**
 * Home's cards: Right now, Needs a word, the next live, Your rooms, and the results (who registered and how they
 * moved). Ported from the prototype's v-home.js; every number is the server's.
 */
import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router";
import type {
  AutomationSummary,
  HomeChain,
  HomeChainRoom,
  HomeChainStep,
  HomeFault,
  HomeNeed,
  HomeNextLive,
  HomeClicked,
  HomePerson,
  HomeRange,
  HomeRegistered,
  HomeRightNow,
  HomeWatching,
  HomeRoom,
  HomeWho,
  RoomStage,
} from "../../../shared/types";
import { openedShare } from "../../../shared/automation-words";
import { boardPath } from "../../board/paths";
import { FlowLine, summaryLine } from "../../components/FlowLine";
import { Button, IconButton } from "../../components/ui/Button";
import { Face, FaceStack, Ring } from "../../components/ui/Face";
import { Icon, type IconName } from "../../components/ui/Icon";
import { placeBeside } from "../../components/ui/place";
import { useToast } from "../../components/ui/Toast";
import { Toggle } from "../../components/ui/Toggle";
import { load } from "../../lib/kept";
import { useOpenDirect } from "../../talk/handoff";
import { api } from "../../lib/api";
import { ago, clock, dayMonth, fmt, longDay, shortDay, untilDay } from "../../lib/when";

const face = (p: HomePerson) => ({ name: p.name, photo: p.avatar_url });
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** "6 people are watching right now, and 6 need a word from you.", the line under the hello. */
export function HelloLine({ watching, needs }: { watching: number; needs: number }) {
  return (
    <>
      {watching === 0 ? "No one is watching right now" : <><b>{`${fmt(watching)} ${plural(watching, "person", "people")}`}</b>{` ${watching === 1 ? "is" : "are"} watching right now`}</>}
      {needs === 0 ? ", and no one needs a word from you." : <>, and <b>{`${fmt(needs)} ${needs === 1 ? "needs" : "need"} a word`}</b> from you.</>}
    </>
  );
}

/** Copy a link, and say so. */
export function useCopy(): (url: string) => void {
  const toast = useToast();
  return (url) => {
    void navigator.clipboard?.writeText(url).catch(() => {});
    toast({ text: `Copied: ${url}`, icon: "copy" });
  };
}

// ── right now ───────────────────────────────────────────────────────────
/** What a person watching right now is doing: "18 of 48 min · Spring Listings Masterclass, the replay". */
export function watchingLine(p: HomeWatching): string {
  return `${p.minute} of ${p.of_minutes} min · ${p.webinar_title}${p.replay ? ", the replay" : ""}${p.live ? ", live" : ""}`;
}

/** Where and when someone just registered. */
export function registeredLine(p: HomeRegistered): string {
  return `${p.webinar_title}${p.session_starts_at ? `, ${shortDay(p.session_starts_at)}` : ""} · ${ago(p.at)}`;
}

/** Which offer someone clicked, when, and whether they became a customer. */
export function clickedLine(p: HomeClicked): string {
  return `${p.offer_label} · ${Date.now() / 1000 - p.at < 3600 ? ago(p.at) : clock(p.at)}, ${p.customer ? "became a customer" : "not a customer yet"}`;
}

export function RightNow({ now, at }: { now: HomeRightNow; at: number }) {
  const { watching, registered, clicked } = now;
  const rest = (count: number, shown: number) => Math.max(0, count - shown);
  return (
    <section className="card now" aria-label="Right now">
      <div className="now-h">
        <h2>Right now</h2>
        <span className="livelbl">
          <i />
          Live
        </span>
        <span className="sp" />
        <span className="small muted">{clock(at / 1000)}</span>
      </div>
      <div className="now-cols">
        <div className="now-col">
          <h3>
            <Icon name="eye" /> Watching <span className="n">{fmt(watching.count)}</span>
          </h3>
          {watching.people.map((p) => (
            <Link key={p.id} className="nrow" to={`/people/${p.id}`}>
              <Ring name={p.name} photo={p.avatar_url} size={40} percent={Math.round((p.minute / Math.max(1, p.of_minutes)) * 100)} live />
              <div>
                <div className="nm"><bdi>{p.name}</bdi></div>
                <div className="ds">{watchingLine(p)}</div>
              </div>
            </Link>
          ))}
          {watching.count === 0 ? <div className="nquiet">No one right now</div> : null}
          {rest(watching.count, watching.people.length) > 0 ? (
            <div className="nmore">
              <FaceStack people={watching.more.map(face)} size={24} />
              <span>{`and ${fmt(rest(watching.count, watching.people.length))} more${watching.more_room ? `, all in ${watching.more_room}` : ""}`}</span>
            </div>
          ) : null}
        </div>
        <div className="now-col">
          <h3>
            <Icon name="userplus" /> Just registered <span className="n">{`${fmt(registered.count)} today`}</span>
          </h3>
          {registered.people.map((p) => (
            <Link key={p.id} className="nrow" to={`/people/${p.id}`}>
              <Face name={p.name} photo={p.avatar_url} size={40} />
              <div>
                <div className="nm"><bdi>{p.name}</bdi></div>
                <div className="ds">{registeredLine(p)}</div>
              </div>
            </Link>
          ))}
          {registered.count === 0 ? <div className="nquiet">No one yet today</div> : null}
          {rest(registered.count, registered.people.length) > 0 ? (
            <div className="nmore">
              <FaceStack people={registered.more.map(face)} size={24} />
              <span>{`and ${fmt(rest(registered.count, registered.people.length))} more today`}</span>
            </div>
          ) : null}
        </div>
        <div className="now-col">
          <h3>
            <Icon name="pointer" /> Clicked the offer <span className="n">{`${fmt(clicked.count)} today`}</span>
          </h3>
          {clicked.people.map((p) => (
            <Link key={p.id} className="nrow" to={`/people/${p.id}`}>
              <Face name={p.name} photo={p.avatar_url} size={40} />
              <div>
                <div className="nm"><bdi>{p.name}</bdi></div>
                <div className="ds">{clickedLine(p)}</div>
              </div>
            </Link>
          ))}
          {clicked.count === 0 ? <div className="nquiet">No one yet today</div> : null}
          {rest(clicked.count, clicked.people.length) > 0 ? (
            <div className="nmore">
              <FaceStack people={clicked.more.map(face)} size={24} />
              <span>{`and ${fmt(rest(clicked.count, clicked.people.length))} more today`}</span>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ── needs a word ────────────────────────────────────────────────────────
/** The rows Needs a word shows before it hands the rest to People. */
const NEEDS_SHOWN = 5;

export const KIND: Record<HomeNeed["kind"], { icon: IconName; tone: string }> = {
  question: { icon: "question", tone: "wine" },
  replied: { icon: "mail", tone: "wine" },
  clicked: { icon: "pointer", tone: "brass" },
  quiet: { icon: "hourglass", tone: "" },
};

/** What the asker did about the offer in that room; nothing at none, so nothing untrue is said. */
function liveClicksWords(n: number | undefined): string {
  if (!n) return "";
  return ` Clicked the offer ${n === 1 ? "once" : n === 2 ? "twice" : `${n} times`}.`;
}

export function needWords(n: HomeNeed): { label: string; why: string } {
  switch (n.kind) {
    case "question":
      if (n.where === "live" && n.live_starts_at) {
        const day = longDay(n.live_starts_at).split(" ")[0];
        return { label: `Asked during ${day}'s live`, why: `${shortDay(n.at)}, in the live chat.${liveClicksWords(n.live_clicks)}` };
      }
      return { label: "Asked a question", why: `${ago(n.at)}, in the chat on their page.` };
    case "replied":
      return { label: "Replied to your email", why: `${ago(n.at)}.${n.pressed_play ? "" : " Hasn't pressed play yet."}` };
    case "clicked": {
      const presses = (n.presses ?? 1) > 1 ? ` Clicked it ${n.presses} times.` : "";
      const watched = n.webinar_title ? ` Watched ${n.watched_pct ?? 0}% of ${n.webinar_title}.` : "";
      return { label: "Clicked the offer", why: `${ago(n.at)}, not a customer yet.${watched}${presses}` };
    }
    case "quiet":
      return {
        label: "Went quiet",
        why: n.webinar_title ? `Stopped at ${n.watched_pct ?? 0}% of ${n.webinar_title} on ${dayMonth(n.at)}.` : `Last seen ${dayMonth(n.at)}.`,
      };
  }
}

/** What the one person past the list did, as the design names it: "Noah Brooks also asked a question on Tuesday." */
export function alsoLine(n: HomeNeed): string {
  const name = n.person.name;
  switch (n.kind) {
    case "question":
      return n.where === "live" && n.live_starts_at
        ? `${name} also asked a question on ${longDay(n.live_starts_at).split(" ")[0]}.`
        : `${name} also asked a question ${ago(n.at)}.`;
    case "replied":
      return `${name} also replied to your email.`;
    case "clicked":
      return `${name} also clicked the offer.`;
    case "quiet":
      return `${name} also went quiet.`;
  }
}

/** Opens the Direct thread of the person a need is about; says so when it does not open. */
export function useOpenPerson(): (need: HomeNeed) => void {
  const toast = useToast();
  const openDirect = useOpenDirect();
  return (need) => {
    openDirect(need.person.id).catch(() => toast({ text: "Their conversation didn't open. Try again.", icon: "info" }));
  };
}

export function NeedsCard({
  needs,
  faults,
  onDone,
  onNote,
}: {
  needs: HomeNeed[];
  faults: HomeFault[];
  onDone: (key: string, at: number) => void;
  onNote: (need: HomeNeed) => void;
}) {
  const openPerson = useOpenPerson();
  const [going, setGoing] = useState<Set<string>>(() => new Set());
  const done = (key: string, at: number) => {
    setGoing((g) => new Set(g).add(key));
    window.setTimeout(() => onDone(key, at), 280);
  };
  const shown = needs.slice(0, NEEDS_SHOWN);
  const after = needs.slice(NEEDS_SHOWN);
  const sub = [
    needs.length ? `${fmt(needs.length)} ${plural(needs.length, "person", "people")}` : "",
    faults.length ? `${faults.length} to fix` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const empty = needs.length === 0 && faults.length === 0;
  return (
    <section className="card">
      <div className="card-h">
        <div>
          <h2>Needs a word from you</h2>
          <div className="sub">{sub || "All caught up"}</div>
        </div>
        <Link className="link" to="/conversations">
          All conversations <Icon name="chev" />
        </Link>
      </div>
      <div className="needs mt12">
        {empty ? (
          <div className="card-b">
            <div className="note good">
              <Icon name="check" />
              <div>All caught up</div>
            </div>
          </div>
        ) : null}
        {faults.map((f) => (
          <div key={f.key} className={going.has(f.key) ? "need fault gone" : "need fault"}>
            <div className="needic">
              <Icon name="info" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="row g8 wrap">
                <b>{`${fmt(f.count)} ${plural(f.count, "email", "emails")} didn't arrive`}</b>
                <span className="kind brass">
                  <Icon name="zap" />
                  {f.automation_name}
                </span>
              </div>
              <div className="why">They came back undelivered.</div>
            </div>
            <div className="acts">
              <Link className="btn s p" to={`/automations/${f.automation_id}`}>
                Fix
              </Link>
              <IconButton icon="check" label="Mark as done" small onClick={() => done(f.key, f.at)} />
            </div>
          </div>
        ))}
        {shown.map((n) => {
          const k = KIND[n.kind];
          const w = needWords(n);
          return (
            <div key={n.key} className={going.has(n.key) ? "need gone" : "need"}>
              <Link className="who-l" to={`/people/${n.person.id}`} aria-label={n.person.name}>
                <Face name={n.person.name} photo={n.person.avatar_url} size={46} />
              </Link>
              <div style={{ minWidth: 0 }}>
                <div className="row g8 wrap">
                  <Link className="who-l" to={`/people/${n.person.id}`}>
                    <b>{n.person.name}</b>
                  </Link>
                  <span className={`kind ${k.tone}`}>
                    <Icon name={k.icon} />
                    {w.label}
                  </span>
                </div>
                <div className="why">{w.why}</div>
                {n.quote ? <div className="q">{`“${n.quote}”`}</div> : null}
              </div>
              <div className="acts">
                {n.conversation_id ? (
                  <Button size="s" variant="p" icon="chat" onClick={() => openPerson(n)}>
                    Answer
                  </Button>
                ) : (
                  <Button size="s" variant="soft" icon="send" onClick={() => onNote(n)}>
                    Send a note
                  </Button>
                )}
                <IconButton icon="check" label="Mark as done" small onClick={() => done(n.key, n.at)} />
              </div>
            </div>
          );
        })}
      </div>
      {after.length ? (
        <div className="card-f small muted">
          <Face name={after[0].person.name} photo={after[0].person.avatar_url} size={24} />
          {after.length === 1 ? (
            <>
              <span>{alsoLine(after[0])}</span>
              {after[0].conversation_id ? (
                <button type="button" className="link" onClick={() => openPerson(after[0])}>
                  Open it
                </button>
              ) : (
                <Link className="link" to={`/people/${after[0].person.id}`}>
                  Open it
                </Link>
              )}
            </>
          ) : (
            <>
              <span>{`${after[0].person.name} and ${fmt(after.length - 1)} more also need a word.`}</span>
              <Link className="link" to="/people?group=need">
                See them
              </Link>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}

// ── the next live ───────────────────────────────────────────────────────
export function NextLiveCard({ live, onInvite }: { live: HomeNextLive; onInvite: () => void }) {
  const copy = useCopy();
  const navigate = useNavigate();
  return (
    <section className="card nextcard">
      <div className={live.poster_url ? "poster" : "poster poster-blank"} style={{ aspectRatio: "auto", height: 150 }}>
        {live.poster_url ? <img src={live.poster_url} alt="" /> : <Icon name="live" />}
        <div className="ov">
          <span className="pill">
            <i />
            {`Next live · ${untilDay(live.starts_at)}`}
          </span>
        </div>
      </div>
      <div className="card-b">
        <div className="eyebrow">Next session</div>
        <Link className="h2 mt4" to={`/rooms/${live.webinar_id}`} style={{ fontSize: 18, display: "block" }}>
          {live.title}
        </Link>
        <div className="muted small">{`${longDay(live.starts_at)}, ${clock(live.starts_at)} · ${live.length_min} min`}</div>
        <div className="aud mt12">
          {live.faces.length ? <FaceStack people={live.faces.slice(0, 7).map(face)} size={30} more={Math.max(0, live.registered - Math.min(7, live.faces.length))} /> : null}
          <div className="t">
            <b>{`${fmt(live.registered)} registered`}</b> so far,
            <br />
            {`${fmt(live.since_yesterday)} since yesterday`}
          </div>
        </div>
        <div className="row g8 mt16 wrap">
          <Button size="s" icon="link" onClick={() => copy(live.register_url)}>
            Copy link
          </Button>
          <Button size="s" icon="userplus" onClick={onInvite}>
            Invite people
          </Button>
          <Button size="s" variant="q" onClick={() => navigate(`/rooms/${live.webinar_id}/studio`)}>
            Open the studio
          </Button>
        </div>
      </div>
    </section>
  );
}

// ── your rooms ──────────────────────────────────────────────────────────
const STEP_NAME: Record<string, string> = { page: "The page", room: "In the room", followups: "Follow-ups" };

export function RoomsCard({ rooms }: { rooms: HomeRoom[] }) {
  const navigate = useNavigate();
  const copy = useCopy();
  const copyBtn = (url: string | null) =>
    url ? (
      <Button
        size="s"
        icon="link"
        onClick={(e) => {
          e.stopPropagation();
          copy(url);
        }}
      >
        Copy link
      </Button>
    ) : null;
  const go = <span className="yr-go"><Icon name="chev" /></span>;
  return (
    <section className="card">
      <div className="card-h">
        <h2>Your rooms</h2>
        <Link className="link" to="/rooms">
          All rooms <Icon name="chev" />
        </Link>
      </div>
      <div className="yrooms">
        {rooms.map((r) => {
          const draft = r.state === "draft";
          const pic = draft ? (
            <span className="yr-pic yr-draft">
              <Icon name="pencil" />
            </span>
          ) : r.poster_url ? (
            <img className="yr-pic" src={r.poster_url} alt="" />
          ) : (
            <span className="yr-pic yr-blank">
              <Icon name="play" />
            </span>
          );
          let state: React.ReactNode;
          let act: React.ReactNode = go;
          switch (r.state) {
            case "draft":
              state = `Draft · ${r.draft_step && STEP_NAME[r.draft_step] ? `${STEP_NAME[r.draft_step]} · ` : ""}${r.looks_right} of 3 look right`;
              act = (
                <Button
                  size="s"
                  variant="p"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(boardPath(r.id));
                  }}
                >
                  Continue
                </Button>
              );
              break;
            case "replay":
              state = <span className="yr-ok">Replay ready</span>;
              act = copyBtn(r.register_url);
              break;
            case "next_live":
            case "scheduled":
              state = r.next_at ? `Next live · ${shortDay(r.next_at)}, ${clock(r.next_at)}` : "Live";
              break;
            case "live_now":
              state = (
                <span className="yr-live">
                  <i />
                  Live now
                </span>
              );
              break;
            case "watching":
              state = (
                <span className="yr-live">
                  <i />
                  {`${fmt(r.watching)} watching now`}
                </span>
              );
              act = copyBtn(r.register_url);
              break;
            case "any_time":
              state = "Watch any time";
              act = copyBtn(r.register_url);
              break;
          }
          return (
            // biome-ignore lint/a11y/useSemanticElements: a row that holds its own buttons cannot be one button
            <div
              key={r.id}
              className="yroom"
              role="link"
              tabIndex={0}
              data-opens={`webinar:${r.id}`}
              onClick={() => navigate(draft ? boardPath(r.id) : `/rooms/${r.id}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target === e.currentTarget) navigate(draft ? boardPath(r.id) : `/rooms/${r.id}`);
              }}
            >
              {pic}
              <div className="yr-t">
                <b dir="auto">{r.title}</b>
                <span>{state}</span>
              </div>
              {act}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── your follow-ups ─────────────────────────────────────────────────────
/** The follow-ups Home shows: the ones that run, the busiest first. */
const FOLLOW_UPS_SHOWN = 3;
const inItNow = (a: AutomationSummary) => Object.values(a.line.here).reduce((n, h) => n + h.count, 0);

/** The business's automations, null until they load; a failed load reads as none. */
export function useAutomations(): [AutomationSummary[] | null, React.Dispatch<React.SetStateAction<AutomationSummary[] | null>>] {
  const [autos, setAutos] = useState<AutomationSummary[] | null>(null);
  useEffect(() => {
    let alive = true;
    load("automations:", () => api.automations.list())
      .then((r) => alive && setAutos(r.automations))
      .catch(() => alive && setAutos([]));
    return () => {
      alive = false;
    };
  }, []);
  return [autos, setAutos];
}

export function FollowUps() {
  const toast = useToast();
  const navigate = useNavigate();
  const [autos, setAutos] = useAutomations();
  const shown = (autos ?? [])
    .filter((a) => a.status !== "draft")
    .toSorted((a, b) => inItNow(b) - inItNow(a))
    .slice(0, FOLLOW_UPS_SHOWN);
  if (!shown.length) return null;
  const switchOn = (a: AutomationSummary, on: boolean) => {
    const status = on ? "on" : "off";
    setAutos((list) => list?.map((x) => (x.id === a.id ? { ...x, status } : x)) ?? list);
    toast({ text: on ? `${a.name} is on.` : `${a.name} is off.`, icon: on ? "check" : "pause" });
    api.automations.update(a.id, { status }).catch(() => {
      toast({ text: "The switch did not save. Try again.", icon: "info" });
      setAutos((list) => list?.map((x) => (x.id === a.id ? { ...x, status: a.status } : x)) ?? list);
    });
  };
  return (
    <section className="card mt24">
      <div className="card-h">
        <div>
          <h2>Your follow-ups</h2>
        </div>
        <Link className="link" to="/automations">
          All automations <Icon name="chev" />
        </Link>
      </div>
      <div className="card-b grid g3">
        {shown.map((a) => (
          <div
            key={a.id}
            className="card flat pad afol"
            role="link"
            tabIndex={0}
            onClick={() => navigate(`/automations/${a.id}`)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target === e.currentTarget) navigate(`/automations/${a.id}`);
            }}
          >
            <div className="row">
              <b className="sp">{a.name}</b>
              {/* Why: the switch turns the automation on or off; it does not open it. */}
              <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <Toggle on={a.status === "on"} onChange={(on) => switchOn(a, on)} label={`${a.status === "on" ? "Turn off" : "Turn on"} ${a.name}`} />
              </span>
            </div>
            <FlowLine {...summaryLine(a)} compact />
            <div className="afol-n">
              <b>{`${fmt(inItNow(a))} in it now`}</b>
              {a.enroll ? ` · ${fmt(a.enroll.entered)} joined` : ""}
              {openedShare(a.enroll) === null ? "" : ` · ${openedShare(a.enroll)}% opened`}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── results: who registered, and how they moved ──────────────────────────
export function WhoCard({ who, newOnly }: { who: HomeWho; newOnly: boolean }) {
  const all = newOnly ? "/people?group=new" : "/people";
  const top = Math.max(1, ...who.sources.map((s) => s.count));
  return (
    <section className="card whocard">
      <div className="card-h">
        <h2>Who registered</h2>
        <Link className="link" to={all}>
          {`See all ${fmt(who.total)}`} <Icon name="chev" />
        </Link>
      </div>
      <div className="card-b">
        {who.total === 0 ? (
          <div className="small muted">No one registered in this time.</div>
        ) : (
          <>
            <div className="newrow">
              {who.faces.map((p) => (
                <Link key={p.id} className="newp" to={`/people/${p.id}`}>
                  <Face name={p.name} photo={p.avatar_url} size={44} />
                  <span>{p.name.split(" ")[0]}</span>
                </Link>
              ))}
              {who.total > who.faces.length ? (
                <Link className="newp" to={all}>
                  <span className="face more" style={{ "--s": "44px" } as CSSProperties}>
                    {`+${fmt(who.total - who.faces.length)}`}
                  </span>
                  <span>more</span>
                </Link>
              ) : null}
            </div>
            <div className="srcs">
              <div className="srch">Where they came from</div>
              {who.sources.map((s, i) => (
                <div key={s.label} className="srcrow">
                  <span className="ink2">{s.label}</span>
                  <div className={i ? "bar brassb" : "bar"}>
                    <i style={{ width: `${(s.count / top) * 100}%` }} />
                  </div>
                  <b className="tr">{fmt(s.count)}</b>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export const STEPS: Array<{ key: HomeChainStep; label: string; stage: RoomStage }> = [
  { key: "registered", label: "Registered", stage: "registered" },
  { key: "came", label: "Came", stage: "came" },
  { key: "reached", label: "Reached the offer", stage: "reached" },
  { key: "clicked", label: "Clicked the offer", stage: "clicked" },
  { key: "customers", label: "Became customers", stage: "signed" },
];
const STEP_WORDS = ["came", "reached it", "clicked", "became customers"];

/** The band under the chain: each step's height from its share of everyone, joined by curves. */
function bandPath(values: number[]): string {
  const c = 33;
  const top0 = Math.max(1, values[0]);
  const H = values.map((n) => Math.max(7, 62 * Math.sqrt(n / top0)));
  let top = `M0 ${c - H[0] / 2}`;
  let bot = "";
  for (let i = 0; i < 5; i++) {
    const x1 = i * 200 + 200;
    top += ` L${x1 - 30} ${c - H[i] / 2}`;
    if (i < 4) top += ` C${x1} ${c - H[i] / 2} ${x1} ${c - H[i + 1] / 2} ${x1 + 30} ${c - H[i + 1] / 2}`;
  }
  for (let i = 4; i >= 0; i--) {
    const x0 = i * 200;
    bot += ` L${x0 + 30} ${c + H[i] / 2}`;
    if (i > 0) bot += ` C${x0} ${c + H[i] / 2} ${x0} ${c + H[i - 1] / 2} ${x0 - 30} ${c + H[i - 1] / 2}`;
  }
  return `${top} L1000 ${c - H[4] / 2} L1000 ${c + H[4] / 2}${bot} L0 ${c + H[0] / 2} Z`;
}

export const pct = (a: number, b: number) => (a > 0 ? `${Math.round((b / a) * 100)}%` : "0%");

export function FunnelCard({ chain, range }: { chain: HomeChain; range: HomeRange }) {
  const [pick, setPick] = useState<{ step: (typeof STEPS)[number]; at: DOMRect } | null>(null);
  const on = chain.customers_connected;
  const N = [chain.registered, chain.came, chain.reached, chain.clicked, chain.customers];
  const last = on ? 4 : 3;
  const V = on ? N : [...N.slice(0, 4), N[3]];
  return (
    <section className="card funcard">
      <div className="card-b">
        <div className="fun">
          {STEPS.map((s, i) =>
            i === 4 && !on ? (
              <div key={s.key} className="fstep fstep-off">
                <div className="fz" />
                <div className="fn">
                  <Icon name="link" />
                </div>
                <div className="fl">Became customers</div>
                <Link className="link" to="/settings#ingest">
                  Connect your booking app
                </Link>
              </div>
            ) : (
              <button key={s.key} type="button" className="fstep" onClick={(e) => setPick({ step: s, at: e.currentTarget.getBoundingClientRect() })}>
                <div className="fz">
                  <FaceStack people={chain.faces[s.key].map(face)} size={28 - i} />
                </div>
                <div className="fn">{fmt(N[i])}</div>
                <div className="fl">{s.label}</div>
                {i === 4 ? <div className="fs">From your booking app</div> : null}
              </button>
            ),
          )}
        </div>
        <div className="fbandw">
          <svg className="fband" viewBox="0 0 1000 66" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id="fg" x1="0" x2="1">
                <stop offset="0" stopColor="var(--wine-ink)" stopOpacity=".9" />
                <stop offset=".7" stopColor="var(--wine-ink)" stopOpacity=".55" />
                <stop offset="1" stopColor="var(--brass)" />
              </linearGradient>
            </defs>
            <path d={bandPath(V)} fill="url(#fg)" />
          </svg>
          {[0, 1, 2, 3].slice(0, last).map((i) => (
            <div key={i} className="fconv" style={{ left: `${(i + 1) * 20}%` }}>
              <b>{`${pct(N[i], N[i + 1])} ${STEP_WORDS[i]}`}</b>
            </div>
          ))}
        </div>
        <div className="flegend">
          <span>{on ? `${fmt(N[4])} of ${fmt(N[0])} became customers` : `${fmt(N[3])} of ${fmt(N[0])} clicked the offer`}</span>
          <span>
            <b className="wine">{`${N[0] > 0 ? ((N[last] / N[0]) * 100).toFixed(1) : "0.0"}%`}</b> end to end
          </span>
        </div>
      </div>
      {pick ? <FunnelPick step={pick.step} at={pick.at} rooms={chain.rooms} range={range} onClose={() => setPick(null)} /> : null}
    </section>
  );
}

/**
 * The chain covers every room; a stage belongs to one, so a step asks which room. Each room's count is its share of
 * the same step in the same period, so the rooms add up to the step, and the room's people open for that period.
 */
function FunnelPick({
  step,
  at,
  rooms,
  range,
  onClose,
}: {
  step: (typeof STEPS)[number];
  at: DOMRect;
  rooms: HomeChainRoom[];
  range: HomeRange;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const box = useRef<HTMLDivElement>(null);
  const [spot, setSpot] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) onClose();
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [onClose]);

  useLayoutEffect(() => {
    if (box.current) setSpot(placeBeside(at, { width: box.current.offsetWidth, height: box.current.offsetHeight }, "left"));
  }, [at]);

  return createPortal(
    <div ref={box} className="menu fpick" role="menu" aria-label={`${step.label}, in which room?`} style={spot ?? { visibility: "hidden", top: 0, left: 0 }}>
      <div className="mh">{`${step.label}, in which room?`}</div>
      {rooms.map((r) => (
        <button
          key={r.id}
          type="button"
          role="menuitem"
          onClick={() => {
            onClose();
            navigate(roomStagePath(r.id, step.stage, range));
          }}
        >
          {r.poster_url ? <img src={r.poster_url} alt="" /> : <span className="fp-blank" />}
          <span className="sp" dir="auto">{r.title}</span>
          <b>{fmt(r.steps[step.key])}</b>
        </button>
      ))}
    </div>,
    document.body,
  );
}

/** A room's people at one stage, for Home's period: "/people?room=web_spring&stage=clicked&range=30d". */
export function roomStagePath(room: string, stage: RoomStage, range: HomeRange): string {
  return `/people?room=${encodeURIComponent(room)}&stage=${stage}${range === "all" ? "" : `&range=${range}`}`;
}
